import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileSpreadsheet, Printer, RefreshCw, Search, SlidersHorizontal, X } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import MainService from '../../services/MainService';
import { getPrintHeaderHtml } from '../../services/printHeaderTemplateService';
import {
  FETCH_CHUNK,
  REPORT_TABLE_HEADER_BG,
  REPORT_TABLE_HEADER_BORDER,
  REPORT_TABLE_HEADER_TEXT,
  applyExcelHeaderRowStyle,
  printHtmlInSameTab,
} from './reportPrintHelpers';
import ClientListGrid, { useClientListGrid } from '../ui/ClientListGrid';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'BOOKED', label: 'Booked' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'PICKED', label: 'Picked' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'RETURNED', label: 'Returned' },
];

const todayInput = () => new Date().toISOString().slice(0, 10);

const formatDate = (d) => {
  if (d == null || d === '') return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) {
    const s = String(d);
    return s.length >= 10 ? s.slice(0, 10) : s;
  }
  return dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const statusLabel = (s) => STATUS_OPTIONS.find((o) => o.value === s)?.label || s || '—';

/**
 * @param {'CLIENT'|'CUSTOMER'} requestType
 */
function defaultFilters(requestType) {
  return {
    dateFrom: todayInput(),
    dateTo: todayInput(),
    isPreferredPickupDate: false,
    keyword: '',
    status: '',
    emirateId: '',
    locationId: '',
    driverId: '',
    requestType,
  };
}

function buildPayload(skip, take, f) {
  const d0 = new Date(`${f.dateFrom}T12:00:00`);
  const d1 = new Date(`${f.dateTo}T12:00:00`);
  return {
    RequestType: f.requestType,
    Skip: skip,
    Take: take,
    FromYear: d0.getFullYear(),
    FromMonth: d0.getMonth() + 1,
    FromDay: d0.getDate(),
    ToYear: d1.getFullYear(),
    ToMonth: d1.getMonth() + 1,
    ToDay: d1.getDate(),
    IsPreferredPickupDate: !!f.isPreferredPickupDate,
    Keyword: (f.keyword || '').trim() || null,
    Status: f.status || null,
    EmirateId: f.emirateId ? Number(f.emirateId) : null,
    LocationId: f.locationId ? Number(f.locationId) : null,
    DriverId: f.driverId ? Number(f.driverId) : null,
  };
}

function rowToFlat(r, i, isClient) {
  const party = isClient ? r.Client ?? r.client : r.Customer ?? r.customer;
  return {
    '#': i + 1,
    RefNo: r.RefNo ?? r.refNo ?? '',
    Date: formatDate(r.Date ?? r.date),
    PreferredPickupDate: formatDate(r.PreferredPickupDate ?? r.preferredPickupDate),
    PartyName: party?.Name ?? party?.name ?? '',
    PartyMobile: party?.MobileNo1 ?? party?.mobileNo1 ?? '',
    ContactName: r.ContactName ?? r.contactName ?? '',
    ContactMobile: r.ContactMobile ?? r.contactMobile ?? '',
    Emirate: r.Emirate?.Name ?? r.emirate?.Name ?? r.emirate?.name ?? '',
    Location: r.Location?.Name ?? r.location?.Name ?? r.location?.name ?? '',
    Address: r.Address ?? r.address ?? '',
    Pieces: r.Pieces ?? r.pieces ?? '',
    Driver: r.Driver?.Name ?? r.driver?.Name ?? r.driver?.name ?? '',
    Status: statusLabel(r.Status ?? r.status),
    Remarks: r.Remarks ?? r.remarks ?? '',
  };
}

function rowMatchesTableSearch(r, q, isClient) {
  if (!q) return true;
  const o = rowToFlat(r, 0, isClient);
  const { '#': _n, ...rest } = o;
  return Object.values(rest)
    .map((x) => String(x ?? '').toLowerCase())
    .join(' ')
    .includes(q);
}

const PRINT_COLUMN_DEFS = [
  { key: '#', label: '#' },
  { key: 'RefNo', label: 'Ref' },
  { key: 'Date', label: 'Created' },
  { key: 'PreferredPickupDate', label: 'Preferred pickup' },
  { key: 'PartyName', label: 'Party' },
  { key: 'PartyMobile', label: 'Party mob' },
  { key: 'ContactName', label: 'Contact' },
  { key: 'ContactMobile', label: 'Contact mob' },
  { key: 'Emirate', label: 'Emirate' },
  { key: 'Location', label: 'Location' },
  { key: 'Address', label: 'Address' },
  { key: 'Pieces', label: 'Pieces' },
  { key: 'Driver', label: 'Driver' },
  { key: 'Status', label: 'Status' },
  { key: 'Remarks', label: 'Remarks' },
];

/**
 * Shared Client / Customer Pickup Request report (filters + Excel + PDF print).
 * @param {{ requestType: 'CLIENT'|'CUSTOMER', title: string }} props
 */
export default function PickupRequestReport({ requestType, title }) {
  const isClient = requestType === 'CLIENT';
  const partyLabel = isClient ? 'Client' : 'Customer';

  const columnDefs = useMemo(
    () =>
      PRINT_COLUMN_DEFS.map((c) =>
        c.key === 'PartyName'
          ? { ...c, label: partyLabel }
          : c.key === 'PartyMobile'
            ? { ...c, label: `${partyLabel} mob` }
            : c
      ),
    [partyLabel]
  );

  const gridColumns = useMemo(
    () =>
      columnDefs
        .filter((c) => c.key !== '#')
        .map((c) => ({
          field: c.key,
          title: c.label,
          hideable: true,
          align: c.key === 'Pieces' ? 'right' : 'left',
        })),
    [columnDefs]
  );

  const [appliedFilters, setAppliedFilters] = useState(() => defaultFilters(requestType));
  const [draft, setDraft] = useState(() => defaultFilters(requestType));
  const [filterModalOpen, setFilterModalOpen] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState(null);
  const [tableSearch, setTableSearch] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [excelExporting, setExcelExporting] = useState(false);
  const [printPreparing, setPrintPreparing] = useState(false);
  const [error, setError] = useState('');
  const [emirates, setEmirates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [em, drv] = await Promise.all([
          MainService.EmiratesInDropDown(),
          MainService.DriversInDropDown(),
        ]);
        setEmirates(Array.isArray(em) ? em : []);
        setDrivers(Array.isArray(drv) ? drv : []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!draft.emirateId) {
      setLocations([]);
      return;
    }
    (async () => {
      try {
        const locs = await MainService.LocationByEmirateId(draft.emirateId);
        setLocations(Array.isArray(locs) ? locs : []);
      } catch (e) {
        setLocations([]);
      }
    })();
  }, [draft.emirateId]);

  useEffect(() => {
    if (filterModalOpen) setDraft({ ...appliedFilters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModalOpen]);

  const loadReport = useCallback(async (f = appliedFilters) => {
    setLoading(true);
    setHasLoaded(false);
    setLoadProgress({ loaded: 0, total: 0 });
    setRows([]);
    setTotal(0);
    setError('');
    try {
      let skip = 0;
      let serverTotal = 0;
      const accumulated = [];
      for (;;) {
        const body = buildPayload(skip, FETCH_CHUNK, f);
        const res = await MainService.PickupRequestReport(body);
        const chunk = res?.Data ?? res?.data ?? [];
        const arr = Array.isArray(chunk) ? chunk : [];
        serverTotal = Number(res?.Total ?? res?.total ?? 0) || 0;
        accumulated.push(...arr);
        setRows([...accumulated]);
        setTotal(serverTotal);
        setLoadProgress({ loaded: accumulated.length, total: serverTotal });
        if (arr.length === 0) break;
        skip += arr.length;
        if (skip >= serverTotal) break;
        if (arr.length < FETCH_CHUNK) break;
      }
      setHasLoaded(true);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to load pickup request report.');
      setRows([]);
      setTotal(0);
      setHasLoaded(false);
    } finally {
      setLoading(false);
      setLoadProgress(null);
    }
  }, [appliedFilters]);

  const applyModalFilters = () => {
    if (!draft.dateFrom || !draft.dateTo) {
      setError('From and To dates are required');
      return;
    }
    if (draft.dateFrom > draft.dateTo) {
      setError('From date cannot be after To date');
      return;
    }
    const next = { ...draft, requestType };
    setAppliedFilters(next);
    setFilterModalOpen(false);
    setTableSearch('');
    loadReport(next);
  };

  const searchQ = tableSearch.trim().toLowerCase();
  const filteredRows = useMemo(() => {
    if (!searchQ) return rows;
    return rows.filter((r) => rowMatchesTableSearch(r, searchQ, isClient));
  }, [rows, searchQ, isClient]);

  const getCellValue = useCallback(
    (row, field) => {
      const o = rowToFlat(row, 0, isClient);
      const raw = o[field];
      if (raw == null || raw === '') return '—';
      return String(raw);
    },
    [isClient]
  );

  const grid = useClientListGrid({ columns: gridColumns, rows: filteredRows, getCellValue });

  const filterSummary = useMemo(() => {
    const parts = [
      `${appliedFilters.dateFrom} → ${appliedFilters.dateTo}`,
      appliedFilters.isPreferredPickupDate ? 'preferred pickup date' : 'created date',
    ];
    if (appliedFilters.status) parts.push(statusLabel(appliedFilters.status));
    if (appliedFilters.keyword) parts.push(`keyword`);
    if (appliedFilters.emirateId) parts.push('emirate');
    if (appliedFilters.locationId) parts.push('location');
    if (appliedFilters.driverId) parts.push('driver');
    return parts.join(' · ');
  }, [appliedFilters]);

  const onExportExcel = async () => {
    if (!hasLoaded || loading) {
      setError('Wait for the report to finish loading, then export.');
      return;
    }
    setExcelExporting(true);
    setError('');
    try {
      if (!filteredRows.length) {
        setError('No rows to export.');
        return;
      }
      const sheetData = filteredRows.map((r, i) => rowToFlat(r, i, isClient));
      const ws = XLSX.utils.json_to_sheet(sheetData);
      applyExcelHeaderRowStyle(ws);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, isClient ? 'Client pickup' : 'Customer pickup');
      const fname = `${isClient ? 'client' : 'customer'}-pickup-request-${appliedFilters.dateFrom}-${appliedFilters.dateTo}.xlsx`;
      XLSX.writeFile(wb, fname);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Excel export failed.');
    } finally {
      setExcelExporting(false);
    }
  };

  const onPrintPdf = async () => {
    if (!hasLoaded || loading) {
      setError('Wait for the report to finish loading, then print.');
      return;
    }
    if (!filteredRows.length) {
      setError('No rows to print.');
      return;
    }
    setPrintPreparing(true);
    setError('');
    try {
      const headerHtml = (await getPrintHeaderHtml()) || '';
      const th = columnDefs
        .map(
          (c) =>
            `<th style="background:${REPORT_TABLE_HEADER_BG};color:${REPORT_TABLE_HEADER_TEXT};border:1px solid ${REPORT_TABLE_HEADER_BORDER};padding:6px 8px;font-size:11px;text-align:left;">${c.label}</th>`
        )
        .join('');
      const body = filteredRows
        .map((r, i) => {
          const flat = rowToFlat(r, i, isClient);
          const tds = columnDefs
            .map(
              (c) =>
                `<td style="border:1px solid #e5e7eb;padding:5px 8px;font-size:11px;">${String(flat[c.key] ?? '').replace(/</g, '&lt;')}</td>`
            )
            .join('');
          return `<tr>${tds}</tr>`;
        })
        .join('');
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${title}</title>
<style>@page{size:A4 landscape;margin:12mm;} body{font-family:system-ui,sans-serif;color:#111;} table{width:100%;border-collapse:collapse;}</style>
</head><body>${headerHtml}
<p style="font-size:12px;color:#4b5563;margin:8px 0 12px;">${filteredRows.length} row(s) · ${filterSummary}</p>
<table><thead><tr>${th}</tr></thead><tbody>${body}</tbody></table>
</body></html>`;
      await printHtmlInSameTab(html, title);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Print failed.');
    } finally {
      setPrintPreparing(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {hasLoaded ? filterSummary : 'Set filters and run the report.'}
            {hasLoaded ? ` · ${total} total` : ''}
            {tableSearch.trim() && hasLoaded ? ` · showing ${filteredRows.length} on screen` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setFilterModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters
          </button>
          <button
            type="button"
            onClick={() => hasLoaded && loadReport(appliedFilters)}
            disabled={!hasLoaded || loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={onExportExcel}
            disabled={!hasLoaded || loading || excelExporting}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {excelExporting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Excel
          </button>
          <button
            type="button"
            onClick={onPrintPdf}
            disabled={!hasLoaded || loading || printPreparing}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
          >
            {printPreparing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            PDF / Print
          </button>
        </div>
      </div>

      {hasLoaded && (
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            placeholder="Search in loaded rows…"
            className="h-10 w-full rounded-lg border border-gray-300 py-2 pl-9 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {tableSearch && (
            <button
              type="button"
              onClick={() => setTableSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {loading && (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-600">
          <RefreshCw className="mx-auto mb-2 h-6 w-6 animate-spin text-primary" />
          Loading…
          {loadProgress?.total > 0 ? ` ${loadProgress.loaded} / ${loadProgress.total}` : ''}
        </div>
      )}

      {hasLoaded && !loading && (
        <ClientListGrid
          columns={gridColumns}
          grid={grid}
          emptyText="No pickup requests for these filters."
          minWidth="1400px"
        />
      )}

      {filterModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Report filters</h2>
              <button type="button" onClick={() => setFilterModalOpen(false)}>
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-2">
              <label className="block text-sm sm:col-span-2">
                <span className="mb-1 block font-medium text-gray-700">Date by</span>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={draft.isPreferredPickupDate ? 'preferred' : 'created'}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, isPreferredPickupDate: e.target.value === 'preferred' }))
                  }
                >
                  <option value="created">Created date</option>
                  <option value="preferred">Preferred pickup date</option>
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">From date</span>
                <input
                  type="date"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={draft.dateFrom}
                  onChange={(e) => setDraft((p) => ({ ...p, dateFrom: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">To date</span>
                <input
                  type="date"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={draft.dateTo}
                  onChange={(e) => setDraft((p) => ({ ...p, dateTo: e.target.value }))}
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="mb-1 block font-medium text-gray-700">Keyword</span>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder={`${partyLabel} name/mob, emirate, location, status, ref…`}
                  value={draft.keyword}
                  onChange={(e) => setDraft((p) => ({ ...p, keyword: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Status</span>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={draft.status}
                  onChange={(e) => setDraft((p) => ({ ...p, status: e.target.value }))}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value || 'all'} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Driver</span>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={draft.driverId}
                  onChange={(e) => setDraft((p) => ({ ...p, driverId: e.target.value }))}
                >
                  <option value="">All</option>
                  {drivers.map((d) => (
                    <option key={d.Id} value={d.Id}>{d.Name}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Emirate</span>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={draft.emirateId}
                  onChange={(e) => setDraft((p) => ({ ...p, emirateId: e.target.value, locationId: '' }))}
                >
                  <option value="">All</option>
                  {emirates.map((e) => (
                    <option key={e.Id} value={e.Id}>{e.Name}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Location</span>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={draft.locationId}
                  onChange={(e) => setDraft((p) => ({ ...p, locationId: e.target.value }))}
                  disabled={!draft.emirateId}
                >
                  <option value="">All</option>
                  {locations.map((l) => (
                    <option key={l.Id} value={l.Id}>{l.Name}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
              <button
                type="button"
                onClick={() => setDraft(defaultFilters(requestType))}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => setFilterModalOpen(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyModalFilters}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
              >
                Run report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
